import cn from 'classnames';
import { ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { useMedia } from '@/shared/hooks';

import styles from './Filters.module.scss';

type FilterItem<FilterValue> = {
  value: FilterValue;
  label: ReactNode;
  amount?: number;
};

type FiltersSize = 'normal' | 'small';

type FiltersCommonProps<FilterValue> = {
  items: FilterItem<FilterValue>[];
  className?: string;
  size?: FiltersSize;
  onChange?: (filterKey: FilterValue) => void;
};

type SearchParamFiltersProps<FilterValue> = FiltersCommonProps<FilterValue> & {
  filterName: string;
  defaultActiveKey?: FilterValue;
  value?: never;
};

type ControlledFiltersProps<FilterValue> = FiltersCommonProps<FilterValue> & {
  value: FilterValue;
  filterName?: never;
  defaultActiveKey?: never;
};

type FiltersProps<FilterValue> =
  | SearchParamFiltersProps<FilterValue>
  | ControlledFiltersProps<FilterValue>;

export const Filters = <K extends string>(props: FiltersProps<K>) => {
  const { className, items, size = 'normal', onChange } = props;

  const [searchParams, setSearchParams] = useSearchParams();

  const { isPhone } = useMedia();

  const isSmall = size === 'small';

  const activeKey =
    props.filterName === undefined
      ? props.value
      : ((searchParams.get(props.filterName) || props.defaultActiveKey) as K);

  const handleFiltersChange = (nextValue: K) => {
    if (props.filterName !== undefined) {
      searchParams.set(props.filterName, nextValue);
      setSearchParams(searchParams);
    }

    onChange?.(nextValue);
  };

  return (
    <div className={cn(styles.container, className)} data-section="filter-tabs">
      {items.map((item) => {
        const isItemActive = activeKey === item.value;

        return (
          <button
            key={item.value}
            type="button"
            onClick={() => handleFiltersChange(item.value)}
            className={cn(
              styles.item,
              isSmall && styles.small,
              isItemActive && styles.isSelected,
              isSmall || isPhone ? 'p2' : 'p1',
            )}
          >
            {item.label}
            {item.amount !== undefined && (
              <span
                className={cn(
                  styles.amount,
                  isItemActive && styles.amountActive,
                )}
              >
                {item.amount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
